// components/admin/EmailSettingsModal.js
import React, { useState } from 'react';
import { Mail, Info, X } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { Badge } from '../ui/Badge';
import logger from '../../utils/logger';

const EmailSettingsModal = ({ open, onClose, team, onSubmit }) => {
  const [settings, setSettings] = useState({
    allowExternalSenders: team?.distributionSettings?.allowExternalSenders || false,
    moderationEnabled: team?.distributionSettings?.moderationEnabled || false,
    hideFromGAL: team?.distributionSettings?.hideFromGAL || false,
    moderators: team?.distributionSettings?.moderators || [],
    customEmailAddress: team?.email || '',
    replyToAddress: team?.distributionSettings?.replyToAddress || '',
    messageSize: team?.distributionSettings?.messageSize || 25,
    deliveryMethod: team?.deliverySettings?.deliveryMethod || 'alias',
    syncEnabled: team?.deliverySettings?.syncEnabled ?? true,
    deduplicationEnabled: team?.deliverySettings?.deduplicationEnabled ?? true,
    createCanonicalEmail: team?.deliverySettings?.createCanonicalEmail ?? true,
  });
  const [loading, setLoading] = useState(false);
  const [availableModerators] = useState([]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onSubmit(settings);
      onClose();
    } catch (error) {
      logger.error('Error updating email settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveModerator = (index) => {
    setSettings({
      ...settings,
      moderators: settings.moderators.filter((_, i) => i !== index),
    });
  };

  const handleAddModerator = (e) => {
    const userId = e.target.value;
    if (!userId) return;
    const option = availableModerators.find((m) => m.id === userId || m._id === userId);
    if (option) {
      setSettings({ ...settings, moderators: [...settings.moderators, option] });
    }
    e.target.value = '';
  };

  const Toggle = ({ checked, onChange }) => (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`w-10 h-6 rounded-full transition-colors ${checked ? 'bg-primary' : 'bg-muted'}`}
    >
      <span
        className={`block w-4 h-4 rounded-full bg-white transition-transform ${checked ? 'translate-x-5' : 'translate-x-1'}`}
      />
    </button>
  );

  const footer = (
    <div className="flex justify-end gap-3">
      <Button variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleSubmit} loading={loading} disabled={loading}>
        Save Changes
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      size="lg"
      footer={footer}
      loading={loading}
      title={
        <div className="flex items-center gap-3">
          <Mail className="w-5 h-5 text-info" />
          <div>
            <div className="text-lg font-semibold text-foreground">Email Settings</div>
            <div className="text-sm font-normal text-muted-foreground">{team?.name}</div>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="p-4 rounded-lg bg-muted/40 border border-border">
          <h4 className="text-foreground font-semibold">Basic Settings</h4>

          <div className="mt-3">
            <label className="block text-sm font-medium text-foreground mb-1">Email Address</label>
            <input
              type="text"
              value={settings.customEmailAddress}
              onChange={(e) => setSettings({ ...settings, customEmailAddress: e.target.value })}
              className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div className="mt-3">
            <label className="block text-sm font-medium text-foreground mb-1">Reply-To Address</label>
            <input
              type="text"
              value={settings.replyToAddress}
              onChange={(e) => setSettings({ ...settings, replyToAddress: e.target.value })}
              className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div className="mt-3">
            <label className="block text-sm font-medium text-foreground mb-1">Maximum Message Size (MB)</label>
            <input
              type="number"
              value={settings.messageSize}
              onChange={(e) => setSettings({ ...settings, messageSize: e.target.value })}
              className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>

        <div className="h-px bg-border" />

        <div className="p-4 rounded-lg bg-muted/40 border border-border">
          <h4 className="text-foreground font-semibold">Delivery Settings</h4>

          <div className="mt-3">
            <label className="block text-sm font-medium text-foreground mb-1">Delivery Method</label>
            <select
              value={settings.deliveryMethod}
              onChange={(e) => setSettings({ ...settings, deliveryMethod: e.target.value })}
              className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="alias">Postfix Alias (Recommended)</option>
              <option value="shared_mailbox">Shared Mailbox</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>

          <div className="flex items-center justify-between mt-3">
            <span className="text-sm text-foreground">Enable MongoDB Sync</span>
            <Toggle
              checked={settings.syncEnabled}
              onChange={(checked) => setSettings({ ...settings, syncEnabled: checked })}
            />
          </div>

          <div className="flex items-center justify-between mt-3">
            <span className="text-sm text-foreground">Enable Email Deduplication</span>
            <Toggle
              checked={settings.deduplicationEnabled}
              onChange={(checked) => setSettings({ ...settings, deduplicationEnabled: checked })}
            />
          </div>

          <div className="flex items-center justify-between mt-3">
            <span className="text-sm text-foreground">Create Canonical Team Email</span>
            <Toggle
              checked={settings.createCanonicalEmail}
              onChange={(checked) => setSettings({ ...settings, createCanonicalEmail: checked })}
            />
          </div>

          <div className="flex items-start gap-3 rounded-lg border border-info/30 bg-info/10 text-info p-3 mt-3">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p className="text-xs">
              <strong>Alias Method:</strong> Emails are forwarded to all members via Postfix. Recommended for most use cases.
              <br />
              <strong>Deduplication:</strong> Prevents duplicate emails in team inbox by using messageId.
              <br />
              <strong>Canonical Email:</strong> Creates a single master copy for the team inbox.
            </p>
          </div>
        </div>

        <div className="h-px bg-border" />

        <div className="p-4 rounded-lg bg-muted/40 border border-border">
          <h4 className="text-foreground font-semibold">Permissions & Security</h4>

          <div className="flex items-center justify-between mt-3">
            <span className="text-sm text-foreground">Allow External Senders</span>
            <Toggle
              checked={settings.allowExternalSenders}
              onChange={(checked) => setSettings({ ...settings, allowExternalSenders: checked })}
            />
          </div>

          <div className="flex items-center justify-between mt-3">
            <span className="text-sm text-foreground">Enable Message Moderation</span>
            <Toggle
              checked={settings.moderationEnabled}
              onChange={(checked) => setSettings({ ...settings, moderationEnabled: checked })}
            />
          </div>

          <div className="flex items-center justify-between mt-3">
            <span className="text-sm text-foreground">Hide from Global Address List</span>
            <Toggle
              checked={settings.hideFromGAL}
              onChange={(checked) => setSettings({ ...settings, hideFromGAL: checked })}
            />
          </div>
        </div>

        {settings.moderationEnabled && (
          <div className="p-4 rounded-lg bg-muted/40 border border-border">
            <h4 className="text-foreground font-semibold">Moderators</h4>

            {settings.moderators.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {settings.moderators.map((option, index) => (
                  <Badge key={option.id || option._id || index} variant="secondary" className="pr-1">
                    {option.firstName} {option.lastName}
                    <button
                      type="button"
                      onClick={() => handleRemoveModerator(index)}
                      className="ml-1 rounded-full hover:bg-accent"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}

            <div className="mt-3">
              <label className="block text-sm font-medium text-foreground mb-1">Select Moderators</label>
              <select
                defaultValue=""
                onChange={handleAddModerator}
                className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="" disabled>
                  Search users...
                </option>
                {availableModerators.map((option) => (
                  <option key={option.id || option._id} value={option.id || option._id}>
                    {option.firstName} {option.lastName} ({option.email})
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="flex items-start gap-3 rounded-lg border border-info/30 bg-info/10 text-info p-3">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span className="text-sm">Changes to email settings may take up to 24 hours to fully propagate.</span>
        </div>
      </div>
    </Modal>
  );
};

export default EmailSettingsModal;
